import { ApiError } from '@mc/axios-api';
// @ts-ignore
import {
  showApiError,
  showInfo,
  showUnknownApiError,
  showValidationApiError,
} from '@mc/ui-v4';

/**
 * Slouží pro ošetření chyb při volání akce na api - např. akce po stisknutí tlačítka na formuláři.
 * V případě chyby se zobrazí info modal s podrobnostmi o chybě.
 * @param process api call metoda
 * @return promise na result z api
 */
export function actionApiCall<T>(process: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    process()
      .then((result) => {
        resolve(result);
      })
      .catch((_error) => {
        if (_error.stack && _error.message) {
          console.error(_error);

          showUnknownApiError();
          reject(_error);
        } else {
          const error = _error as ApiError;

          // Request byl programově přerušen
          if (error.cancelled) {
            reject(_error);
          } else {
            switch (error.status) {
              case 422:
                {
                  const { _error, ...rest } = error.formErrors;
                  const errObj = {
                    ...rest,
                  };
                  if (_error) {
                    errObj['_global'] = _error;
                  }

                  showValidationApiError(error);

                  reject(error);
                }
                break;
              default:
                {
                  showApiError(error);

                  reject(error);
                }
                break;
            }
          }
        }
      });
  });
}

/**
 * Je zde aby nemusela být závislost na RFF.
 */
export const RFF_ERROR = 'FINAL_FORM/form-error';

/**
 * React final form validační chyba.
 */
export type RffValidationErrors =
  | {
      _exception?: string;
      [RFF_ERROR]?: string;
    }
  | any
  | null;

/**
 * Formik nemá globální form error, použita nějaká hodnota.
 */
export const FORMIK_ERROR = 'form-error';

/**
 * Formik validační chyba.
 */
export type FormikValidationErrors =
  | {
      _exception?: string;
      [FORMIK_ERROR]?: string;
    }
  | any
  | null;

/**
 * Metoda pro odchycení a správné zpracování error při submit formuláře.
 *
 * Příklad použití - viz rffFormSubmit
 * @param process zpracování submitů - jedná se o metodu, ve které je nějaké volání submitu
 * @return pokud jsou nějaké chyby, je vrácen objekt s chybami validace, jinak undefined
 */
export function formikFormSubmit(
  process: () => Promise<any>
): Promise<FormikValidationErrors> {
  return formSubmit(process, FORMIK_ERROR);
}

/**
 * Metoda pro odchycení a správné zpracování error při submit formuláře.
 *
 * Příklad použití - viz rffFormSubmit
 * ```
 * onSubmit={async (data, {}) => {
 *   return await rffFormSubmit(async () => {
 *     const result = (await PrestupekApi.updateEditacePrestupkuData2(prestupekId, data)).data;
 *     Modal.hide();
 *     onSubmitSuccess && onSubmitSuccess(result);
 *   });
 * }}
 * ```
 *
 * @param process zpracování submitů - jedná se o metodu, ve které je nějaké volání submitu
 * @param formInlineError pokud je true, tak se chyby, které bybly normálně jako tostr zobrazí dají jako submitError ve formuláři
 */
export function rffFormSubmit(
  process: () => Promise<any>,
  formInlineError?: boolean
): Promise<RffValidationErrors> {
  return formSubmit(process, RFF_ERROR, formInlineError);
}

/**
 * Ošetření error při submit formuláře.
 * Pro validační chyby vrátí errors objekt pro formulář, pokud není chyba, vrátí null
 * @param process buď volání api nebo nějaké async volání kde může nastat exception, obecně nějaký kód, kde je voláno api
 * @param formInlineError pokud je true, tak se chyby, které bybly normálně jako tostr zobrazí dají jako submitError ve formuláři
 * @return vrátí null pokud není chyba, error objekt v případě validačních chyb a exception (reject) v případě, že nastala chyba pro toaster a není třeba ji zobrazovat jako validační
 * @reject ApiError
 */
function formSubmit(
  process: () => Promise<any>,
  globalErrorKey: string,
  formInlineError?: boolean
): Promise<RffValidationErrors> {
  return new Promise<RffValidationErrors>((resolve, reject) => {
    process()
      .then(() => {
        resolve(undefined);
      })
      .catch((_error) => {
        if (_error.stack && _error.message) {
          console.error(_error);

          if (formInlineError) {
            resolve({
              _exception: 'JS global error' + JSON.stringify(_error),
              [globalErrorKey]: 'Nastala chyba při zpracování dat na klientovi',
            });
          } else {
            showInfo({
              title: 'Nastala chyba při zpracování dat na klientovi',
            });
          }

          reject(_error);
        } else {
          const error = _error as ApiError;

          // Request byl programově přerušen
          if (error.cancelled) {
            reject(_error);
          } else {
            switch (error.status) {
              case 422:
                {
                  const { _error, ...rest } = error.formErrors;
                  const errObj = {
                    ...rest,
                  };
                  if (_error) {
                    errObj[globalErrorKey] = _error;
                  }

                  resolve(errObj);
                }
                break;
              default:
                {
                  if (formInlineError) {
                    const errObj: any = {};
                    errObj[globalErrorKey] = error.messageTitle;
                    // if (error.messageDesc) {
                    //   errObj[globalErrorKey] += ` (${error.messageDesc})`;
                    // }
                    resolve(errObj);
                  } else {
                    showApiError(error);

                    reject(error);
                  }
                }
                break;
            }
          }
        }
      });
  });
}
